import { useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Typography, Chip, Alert, CircularProgress, IconButton, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip,
  Autocomplete, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  listWhitelistItems, listCollectors, addWhitelistEntry, removeWhitelistEntry,
  listCreditAccounts, addCreditAccount, removeCreditAccount,
  WhitelistItem, CollectorOption, CreditAccount,
} from "../services/debines";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Whitelist() {
  const [rows,       setRows]       = useState<WhitelistItem[]>([]);
  const [collectors, setCollectors] = useState<CollectorOption[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [busy,       setBusy]       = useState(false);
  const [err,        setErr]        = useState<string | null>(null);
  const [ok,         setOk]         = useState<string | null>(null);
  const [filter,     setFilter]     = useState("");

  // Alta
  const [newCvu,       setNewCvu]       = useState("");
  const [newCollector, setNewCollector] = useState<CollectorOption | null>(null);

  // Baja (confirmación): CVU entero o un colector puntual
  const [confirm, setConfirm] = useState<{ cvu: string; collector?: string } | null>(null);

  // Cuentas de crédito (tabla debin_credit_accounts): CBU + titular libre
  const [creditRows,    setCreditRows]    = useState<CreditAccount[]>([]);
  const [newCbu,        setNewCbu]        = useState("");
  const [newOwner,      setNewOwner]      = useState("");
  const [confirmCredit, setConfirmCredit] = useState<CreditAccount | null>(null);

  async function load() {
    setLoading(true); setErr(null);
    try {
      const [items, cols, credits] = await Promise.all([
        listWhitelistItems(), listCollectors(), listCreditAccounts(),
      ]);
      setRows(items); setCollectors(cols); setCreditRows(credits);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const cvuValid = /^\d{20,22}$/.test(newCvu.trim());
  const cbuValid = /^\d{20,22}$/.test(newCbu.trim());

  async function onAdd() {
    if (!cvuValid || !newCollector) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      await addWhitelistEntry(newCvu.trim(), newCollector.name);
      setOk(`CVU ${newCvu.trim()} habilitada para ${newCollector.name}.`);
      setNewCvu(""); setNewCollector(null);
      await load();
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  async function onRemove() {
    if (!confirm) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      await removeWhitelistEntry(confirm.cvu, confirm.collector);
      setOk(confirm.collector
        ? `Se quitó ${confirm.collector} de la CVU ${confirm.cvu}.`
        : `CVU ${confirm.cvu} eliminada de la whitelist.`);
      setConfirm(null);
      await load();
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  async function onAddCredit() {
    if (!cbuValid || !newOwner.trim()) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      const exists = creditRows.some((r) => r.cbu === newCbu.trim());
      await addCreditAccount(newCbu.trim(), newOwner.trim());
      setOk(exists
        ? `Se actualizó el titular de la cuenta ${newCbu.trim()}.`
        : `Cuenta ${newCbu.trim()} (${newOwner.trim()}) agregada a las cuentas de crédito.`);
      setNewCbu(""); setNewOwner("");
      await load();
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  async function onRemoveCredit() {
    if (!confirmCredit) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      await removeCreditAccount(confirmCredit.cbu);
      setOk(`Cuenta ${confirmCredit.cbu} eliminada de las cuentas de crédito.`);
      setConfirmCredit(null);
      await load();
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return rows;
    return rows.filter((r) =>
      r.cvu.includes(f) || r.collectors.some((c) => c.toLowerCase().includes(f)));
  }, [rows, filter]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Whitelist DEBIN — débito</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        CVUs habilitadas para débito DEBIN (tabla <code>debin_debit_accounts</code>). Una CVU puede
        tener más de un colector. Los DEBINes hacia CVUs fuera de esta lista no muestran el botón Aprobar.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>Agregar CVU / colector</Typography>
        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <TextField size="small" label="CVU" value={newCvu} disabled={busy}
            onChange={(e) => setNewCvu(e.target.value.replace(/\D/g, ""))}
            error={newCvu.trim() !== "" && !cvuValid}
            helperText={newCvu.trim() !== "" && !cvuValid ? "20-22 dígitos" : " "}
            inputProps={{ inputMode: "numeric", maxLength: 22 }} sx={{ width: 260 }} />
          <Autocomplete size="small" sx={{ width: 340 }} options={collectors}
            value={newCollector} onChange={(_, v) => setNewCollector(v)} disabled={busy}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.name === b.name}
            renderOption={(props, o) => (
              <li {...props} key={o.name}>
                <Box>
                  <Typography variant="body2">{o.name}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>{o.tax_id}</Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Colector (buscar)" helperText=" " />
            )} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}
            disabled={busy || !cvuValid || !newCollector} sx={{ mt: 0.25 }}>
            Agregar
          </Button>
        </Stack>
      </Paper>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {ok  && <Alert severity="success" onClose={() => setOk(null)} sx={{ mb: 2 }}>{ok}</Alert>}

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <TextField size="small" label="Filtrar por CVU o colector" value={filter}
          onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 280 }} />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {filtered.length} de {rows.length} CVU(s)
        </Typography>
        <Tooltip title="Refrescar">
          <span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>CVU</TableCell>
              <TableCell>Colector(es)</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Última modificación</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                Sin CVUs en la whitelist.
              </TableCell></TableRow>
            )}
            {filtered.map((r) => (
              <TableRow key={r.cvu} hover>
                <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{r.cvu}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {r.collectors.map((c) => (
                      <Chip key={c} label={c} size="small"
                        onDelete={busy ? undefined : () => setConfirm({ cvu: r.cvu, collector: c })} />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12, color: "text.secondary" }}>
                  {fmtDate(r.updated_at)}{r.updated_by ? ` · ${r.updated_by}` : ""}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Eliminar la CVU entera de la whitelist">
                    <span>
                      <IconButton size="small" color="error" disabled={busy}
                        onClick={() => setConfirm({ cvu: r.cvu })}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, mt: 4 }}>Whitelist DEBIN — crédito</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Cuentas de crédito DEBIN (tabla <code>debin_credit_accounts</code>): CBU/CVU donde se
        acredita el dinero, con el titular como etiqueta. Cada cuenta tiene un solo titular.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>Agregar cuenta de crédito</Typography>
        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <TextField size="small" label="CBU / CVU" value={newCbu} disabled={busy}
            onChange={(e) => setNewCbu(e.target.value.replace(/\D/g, ""))}
            error={newCbu.trim() !== "" && !cbuValid}
            helperText={newCbu.trim() !== "" && !cbuValid ? "20-22 dígitos" : " "}
            inputProps={{ inputMode: "numeric", maxLength: 22 }} sx={{ width: 260 }} />
          <TextField size="small" label="Titular" value={newOwner} disabled={busy}
            onChange={(e) => setNewOwner(e.target.value)}
            helperText="Etiqueta libre (ej. YPF)"
            inputProps={{ maxLength: 80 }} sx={{ width: 340 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={onAddCredit}
            disabled={busy || !cbuValid || !newOwner.trim()} sx={{ mt: 0.25 }}>
            Agregar
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>CBU / CVU</TableCell>
              <TableCell>Titular</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Última modificación</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && creditRows.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loading && creditRows.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                Sin cuentas de crédito.
              </TableCell></TableRow>
            )}
            {creditRows.map((r) => (
              <TableRow key={r.cbu} hover>
                <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{r.cbu}</TableCell>
                <TableCell><Chip label={r.owner || "—"} size="small" /></TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12, color: "text.secondary" }}>
                  {fmtDate(r.updated_at)}{r.updated_by ? ` · ${r.updated_by}` : ""}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Eliminar la cuenta de crédito">
                    <span>
                      <IconButton size="small" color="error" disabled={busy}
                        onClick={() => setConfirmCredit(r)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!confirmCredit} onClose={() => !busy && setConfirmCredit(null)}>
        <DialogTitle>Eliminar cuenta de crédito</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vas a eliminar la cuenta <b>{confirmCredit?.cbu}</b>
            {confirmCredit?.owner ? <> ({confirmCredit.owner})</> : null} de las cuentas de
            crédito DEBIN. ¿Continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCredit(null)} disabled={busy}>Cancelar</Button>
          <Button onClick={onRemoveCredit} disabled={busy} variant="contained" color="error">
            {busy ? "Eliminando…" : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>{confirm?.collector ? "Quitar colector" : "Eliminar CVU de la whitelist"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.collector ? (
              <>Vas a quitar el colector <b>{confirm.collector}</b> de la CVU{" "}
                <b>{confirm?.cvu}</b>. Si era el único, la CVU sale de la whitelist y sus
                DEBINes dejan de poder aprobarse. ¿Continuar?</>
            ) : (
              <>Vas a eliminar la CVU <b>{confirm?.cvu}</b> de la whitelist. Sus DEBINes
                dejan de poder aprobarse desde webadmin2. ¿Continuar?</>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancelar</Button>
          <Button onClick={onRemove} disabled={busy} variant="contained" color="error">
            {busy ? "Eliminando…" : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
